/**
 * W3C Trace Context — https://www.w3.org/TR/trace-context/
 *
 * Implements the `traceparent` header standard used by OpenTelemetry, Datadog,
 * Grafana, Sentry, and every major observability platform.
 *
 * Format: 00-{traceId(32hex)}-{spanId(16hex)}-{flags(2hex)}
 *
 * Each distributed transaction (e.g. user clicking a button) gets one `traceId`
 * that stays the same across ALL hops: browser → Next.js proxy → FastAPI backend.
 * Each hop generates its own `spanId` and records the upstream's span as its parent.
 * This creates a full call tree in any tracing backend.
 *
 * USAGE:
 * - Browser requests: use generateTraceContext() — starts a new trace
 * - Proxy/intermediary: use createChildSpan() — continues an existing trace
 * - All: use buildTracingHeaders() to get the headers to send
 */

// ─── Types ───────────────────────────────────────────────────────────────────

/** A trace context for a new or continuing distributed transaction. */
export interface TraceContext {
  /** 32 hex chars — stable across all hops in a transaction */
  traceId: string;
  /** 16 hex chars — unique to this hop */
  spanId: string;
  /** UUID v4 with dashes — same bits as traceId, human-readable for support tickets */
  requestId: string;
  /** Full W3C traceparent header value: `00-{traceId}-{spanId}-01` */
  traceparent: string;
}

/** A child span context — carries the upstream span ID as `parentSpanId`. */
export interface ChildSpanContext extends TraceContext {
  /** The spanId of the upstream hop (null if this started a new trace). */
  parentSpanId: string | null;
}

// ─── Validation ──────────────────────────────────────────────────────────────

const TRACE_ID_RE = /^[a-f0-9]{32}$/;
const SPAN_ID_RE = /^[a-f0-9]{16}$/;

// ─── Core Functions ───────────────────────────────────────────────────────────

/**
 * Generate a fresh trace context for a new distributed transaction.
 * Call this at the outermost entry point (e.g. browser fetch in api-client.ts).
 */
export function generateTraceContext(): TraceContext {
  const requestId = crypto.randomUUID(); // e.g. 550e8400-e29b-41d4-a716-446655440000
  const traceId = requestId.replace(/-/g, ''); // 32 hex, no dashes
  const spanId = crypto.randomUUID().replace(/-/g, '').slice(0, 16); // 16 hex

  return {
    traceId,
    spanId,
    requestId,
    traceparent: `00-${traceId}-${spanId}-01`,
  };
}

/**
 * Parse an incoming W3C `traceparent` header value.
 * Returns null if the header is absent, malformed, or uses an unknown version.
 */
export function parseTraceparent(
  value: string | null | undefined,
): { traceId: string; spanId: string } | null {
  if (!value) return null;

  const parts = value.split('-');
  if (parts.length !== 4) return null;

  const [version, traceId, spanId] = parts;

  if (version !== '00') return null;
  if (!TRACE_ID_RE.test(traceId)) return null;
  if (!SPAN_ID_RE.test(spanId)) return null;

  return { traceId, spanId };
}

/**
 * Create a child span for an intermediary hop (e.g. a proxy or BFF layer).
 *
 * - If an incoming `traceparent` is valid: preserves the traceId, generates a new
 *   spanId, and records the upstream's spanId as `parentSpanId`.
 * - If none: generates a fresh trace (same as generateTraceContext).
 */
export function createChildSpan(incomingTraceparent: string | null | undefined): ChildSpanContext {
  const newSpanId = crypto.randomUUID().replace(/-/g, '').slice(0, 16);
  const parsed = parseTraceparent(incomingTraceparent ?? null);

  if (parsed) {
    // Continue the existing trace with a new span
    const { traceId, spanId: parentSpanId } = parsed;

    // Re-format traceId as UUID with dashes for X-Request-ID
    const requestId = [
      traceId.slice(0, 8),
      traceId.slice(8, 12),
      traceId.slice(12, 16),
      traceId.slice(16, 20),
      traceId.slice(20),
    ].join('-');

    return {
      traceId,
      spanId: newSpanId,
      requestId,
      traceparent: `00-${traceId}-${newSpanId}-01`,
      parentSpanId,
    };
  }

  // No valid incoming trace — start fresh
  const { traceId, requestId } = generateTraceContext();
  return {
    traceId,
    spanId: newSpanId,
    requestId,
    traceparent: `00-${traceId}-${newSpanId}-01`,
    parentSpanId: null,
  };
}

/**
 * Build the HTTP headers to attach to an outgoing fetch call.
 *
 * Sends two headers:
 * - `traceparent`   — W3C standard, consumed by OTel/Datadog/Grafana/Sentry
 * - `X-Request-ID`  — UUID format, echoed in error responses for support tickets
 */
export function buildTracingHeaders(ctx: TraceContext): Record<string, string> {
  return {
    traceparent: ctx.traceparent,
    'X-Request-ID': ctx.requestId,
  };
}
